import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Download,
  Email as EmailIcon,
  ExpandLess,
  ExpandMore,
  FilterAlt,
  Map as MapIcon,
  Refresh,
  Reply,
  Search,
  TouchApp,
  TrendingUp,
  Visibility,
} from '@mui/icons-material';
import GeographicMapView from './GeographicMapViewEnhanced';

const normalizeTrackingItem = (item, sentLookup, accountLookup, index) => {
  const sentRecord =
    sentLookup.get(`message:${item?.messageId || ''}`) ||
    sentLookup.get(`tracking:${item?.trackingId || ''}`) ||
    null;
  const account = sentRecord?.accountId ? accountLookup.get(sentRecord.accountId) : null;
  const openCount = Number(item?.openCount ?? item?.totalOpens ?? 0) || 0;
  const clickCount = Number(item?.clickCount ?? 0) || 0;
  const sentAt = item?.sentAt || sentRecord?.sentAt || null;
  const recipient = item?.recipient || sentRecord?.recipients?.[0] || '';
  const status = String(item?.status || '').toLowerCase();

  return {
    ...item,
    id: item?.id || item?.messageId || item?.trackingId || `${recipient}-${sentAt || index}`,
    recipient,
    subject: item?.subject || sentRecord?.subject || '',
    accountId: item?.accountId || sentRecord?.accountId || '',
    fromEmail: item?.fromEmail || account?.fromEmail || account?.username || '',
    account: item?.account || account?.label || account?.fromEmail || account?.username || '',
    sentAt,
    openedAt: item?.openedAt || item?.lastOpenedAt || null,
    clickedAt: item?.clickedAt || item?.lastClickedAt || null,
    openCount,
    clickCount,
    opened: Boolean(item?.opened || openCount > 0 || status === 'opened' || status === 'clicked'),
    clicked: Boolean(item?.clicked || clickCount > 0 || status === 'clicked'),
    replied: Boolean(item?.replied),
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatRefreshTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getOpenCount = (email) => {
  const parsed = Number(email?.openCount ?? 0);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return email?.opened ? 1 : 0;
};

const TrackingAnalytics = ({
  trackingData = [],
  sentRecords = [],
  accounts = [],
  emailStats = {},
  campaignAnalytics = {},
  isRefreshing = false,
  refreshError = '',
  lastRefreshedAt = null,
  onRefresh = () => {},
  onExportReport = () => {},
  onShareStats = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedCount, setLoadedCount] = useState(1000);
  const [showMap, setShowMap] = useState(true);
  const itemsPerPage = 25;

  const enrichedTrackingData = useMemo(() => {
    const accountLookup = new Map();
    (accounts || []).forEach((account) => {
      if (account?.id) {
        accountLookup.set(account.id, account);
      }
    });

    const sentLookup = new Map();
    (sentRecords || []).forEach((record) => {
      const messageId = record?.messageId || record?.id;
      if (messageId) {
        sentLookup.set(`message:${messageId}`, record);
      }
      if (record?.trackingId) {
        sentLookup.set(`tracking:${record.trackingId}`, record);
      }
    });

    return (trackingData || []).map((item, index) =>
      normalizeTrackingItem(item, sentLookup, accountLookup, index)
    );
  }, [accounts, sentRecords, trackingData]);

  const totalSent = enrichedTrackingData.length;
  const totalOpened = enrichedTrackingData.filter((item) => item.opened).length;
  const totalClicked = enrichedTrackingData.filter((item) => item.clicked).length;
  const totalReplied = enrichedTrackingData.filter((item) => item.replied).length;

  const openRate = totalSent > 0 ? Math.round((totalOpened / totalSent) * 100) : 0;
  const clickRate = totalSent > 0 ? Math.round((totalClicked / totalSent) * 100) : 0;
  const replyRate = totalSent > 0 ? Math.round((totalReplied / totalSent) * 100) : 0;

  const uniqueAccounts = useMemo(() => {
    const values = new Set();
    enrichedTrackingData.forEach((item) => {
      const account = item.fromEmail || item.account;
      if (account) {
        values.add(account);
      }
    });
    return Array.from(values).sort();
  }, [enrichedTrackingData]);

  const filteredData = useMemo(() => {
    let filtered = enrichedTrackingData.slice(0, loadedCount);
    if (selectedAccount !== 'all') {
      filtered = filtered.filter((item) => (item.fromEmail || item.account) === selectedAccount);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const haystack = [
          item.recipient || '',
          item.subject || '',
          item.campaignName || '',
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    }
    return filtered;
  }, [enrichedTrackingData, loadedCount, searchQuery, selectedAccount]);

  const tabFilteredData = useMemo(() => {
    switch (activeTab) {
      case 1:
        return filteredData.filter((item) => item.opened);
      case 2:
        return filteredData.filter((item) => item.clicked);
      case 3:
        return filteredData.filter((item) => item.replied);
      default:
        return filteredData;
    }
  }, [activeTab, filteredData]);

  const totalPages = Math.max(1, Math.ceil(tabFilteredData.length / itemsPerPage));
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return tabFilteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, tabFilteredData]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleLoadMore = () => {
    setLoadedCount((prev) => Math.min(prev + 1000, totalSent));
    setCurrentPage(1);
  };

  const statCards = [
    {
      label: 'Total Sent',
      value: totalSent,
      icon: <EmailIcon />,
      color: '#667eea',
      bg: 'linear-gradient(135deg, #667eea15, #764ba215)',
    },
    {
      label: 'Opened',
      value: totalOpened,
      sub: `${openRate}%`,
      icon: <Visibility />,
      color: '#10b981',
      bg: 'linear-gradient(135deg, #10b98115, #05966915)',
    },
    {
      label: 'Clicked',
      value: totalClicked,
      sub: `${clickRate}%`,
      icon: <TouchApp />,
      color: '#3b82f6',
      bg: 'linear-gradient(135deg, #3b82f615, #2563eb15)',
    },
    {
      label: 'Replied',
      value: totalReplied,
      sub: `${replyRate}%`,
      icon: <Reply />,
      color: '#f59e0b',
      bg: 'linear-gradient(135deg, #f59e0b15, #d9770615)',
    },
    {
      label: 'Engagement',
      value: `${openRate}%`,
      icon: <TrendingUp />,
      color: '#8b5cf6',
      bg: 'linear-gradient(135deg, #8b5cf615, #7c3aed15)',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card variant="outlined" sx={{ borderRadius: 2.5, border: '1px solid rgba(102,126,234,0.12)' }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Seen Emails
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Live open and click tracking based on your real email records
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {lastRefreshedAt ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Updated ${formatRefreshTime(lastRefreshedAt)}`}
                />
              ) : null}
              <Button
                size="small"
                variant="contained"
                onClick={onRefresh}
                disabled={isRefreshing}
                startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                sx={{ borderRadius: 2 }}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Stack>
          </Stack>
          {refreshError ? (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
              {refreshError}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <Grid container spacing={isMobile ? 1 : 2}>
        {statCards.map((stat) => (
          <Grid item xs={6} sm={4} md key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 1.5 : 2,
                borderRadius: 2.5,
                background: stat.bg,
                border: `1px solid ${stat.color}20`,
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${stat.color}15`,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: `${stat.color}15`,
                  color: stat.color,
                  mx: 'auto',
                  mb: 1,
                }}
              >
                {stat.icon}
              </Avatar>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={800} sx={{ color: stat.color }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {stat.label}
              </Typography>
              {stat.sub ? (
                <Chip
                  label={stat.sub}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: `${stat.color}15`,
                    color: stat.color,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                  }}
                />
              ) : null}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {enrichedTrackingData.length > 0 ? (
        <Card
          variant="outlined"
          sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(102,126,234,0.15)' }}
        >
          <Box
            onClick={() => setShowMap(!showMap)}
            sx={{
              px: isMobile ? 2 : 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea08, #764ba208)',
              borderBottom: showMap ? '1px solid rgba(102,126,234,0.1)' : 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': { background: 'linear-gradient(135deg, #667eea12, #764ba212)' },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#667eea15', color: '#667eea' }}>
                <MapIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Geographic Performance Map
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Map only uses records that actually include state/location data
                </Typography>
              </Box>
            </Stack>
            <IconButton size="small">{showMap ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
          <Collapse in={showMap}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2.5 }}>
              <GeographicMapView trackingData={enrichedTrackingData} />
            </CardContent>
          </Collapse>
        </Card>
      ) : null}

      <Card variant="outlined" sx={{ borderRadius: 2.5, border: '1px solid rgba(102,126,234,0.12)' }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={7}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by recipient, subject, or campaign..."
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setCurrentPage(1);
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={8} sm={4} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Account</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={(event) => {
                    setSelectedAccount(event.target.value);
                    setCurrentPage(1);
                  }}
                  label="Account"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All ({uniqueAccounts.length})</MenuItem>
                  {uniqueAccounts.map((account) => (
                    <MenuItem key={account} value={account}>
                      {account}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={2} md={2}>
              <Chip
                icon={<FilterAlt fontSize="small" />}
                label={`${filteredData.length}`}
                variant="outlined"
                sx={{ width: '100%', fontWeight: 600 }}
              />
            </Grid>
          </Grid>
          {searchQuery || selectedAccount !== 'all' ? (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSearchQuery('');
                setSelectedAccount('all');
                setCurrentPage(1);
              }}
              sx={{ mt: 1 }}
            >
              Clear Filters
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => {
              setActiveTab(value);
              setCurrentPage(1);
            }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
              },
            }}
          >
            <Tab label={`All (${filteredData.length})`} icon={<EmailIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={`Opened (${filteredData.filter((item) => item.opened).length})`} icon={<Visibility sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={`Clicked (${filteredData.filter((item) => item.clicked).length})`} icon={<TouchApp sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={`Replied (${filteredData.filter((item) => item.replied).length})`} icon={<Reply sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
          {paginatedData.length > 0 ? (
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102,126,234,0.06)' }}>
                    <TableCell>
                      <Typography fontWeight={700} variant="caption">
                        Recipient
                      </Typography>
                    </TableCell>
                    {!isMobile ? (
                      <TableCell align="center">
                        <Typography fontWeight={700} variant="caption">
                          Account
                        </Typography>
                      </TableCell>
                    ) : null}
                    <TableCell align="center">
                      <Typography fontWeight={700} variant="caption">
                        Opens
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} variant="caption">
                        Opened
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700} variant="caption">
                        Clicked
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={700} variant="caption">
                        Sent
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((email) => (
                    <TableRow key={email.id} sx={{ '&:hover': { bgcolor: 'rgba(102,126,234,0.04)' } }}>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="caption" fontWeight={600}>
                            {email.recipient || '-'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {email.subject || '(No subject)'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      {!isMobile ? (
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={email.fromEmail || email.account || '-'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell align="center">
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: getOpenCount(email) > 0 ? '#10b981' : '#94a3b8' }}
                        >
                          {getOpenCount(email)}x
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {email.opened ? (
                          <Tooltip title={`Opened: ${formatDate(email.openedAt)}`}>
                            <Chip
                              size="small"
                              icon={<Visibility sx={{ fontSize: 14 }} />}
                              label="Yes"
                              sx={{
                                bgcolor: '#10b981',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {email.clicked ? (
                          <Tooltip title={`Clicked: ${formatDate(email.clickedAt)}`}>
                            <Chip
                              size="small"
                              icon={<TouchApp sx={{ fontSize: 14 }} />}
                              label="Yes"
                              sx={{
                                bgcolor: '#3b82f6',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{formatDate(email.sentAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {enrichedTrackingData.length === 0
                ? 'No tracking data yet. Send emails with tracking enabled to see real activity here.'
                : activeTab === 1
                  ? 'No opened emails yet. They will appear when recipients open them.'
                  : activeTab === 2
                    ? 'No clicked emails yet. They will appear when recipients click links.'
                    : activeTab === 3
                      ? 'No replied emails yet.'
                      : 'No emails match your current filters.'}
            </Alert>
          )}

          {totalPages > 1 ? (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
              <Typography variant="caption" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
            </Stack>
          ) : null}

          {loadedCount < totalSent ? (
            <Stack direction="row" justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLoadMore}
                startIcon={<Download />}
                sx={{ borderRadius: 2 }}
              >
                Load More ({totalSent - loadedCount} remaining)
              </Button>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TrackingAnalytics;
